/**
 * Marcar uma reunião — a tela 6, e a tela que **não** promete integração de calendário.
 *
 * **`calendar` aparece desabilitado, com o motivo por extenso — e nunca clicável.** A medição de
 * 25/08 não encontrou uma linha de uso programático do Google Calendar na organização, nem service
 * account com delegação de domínio; o adaptador está declarado e sem implementação, e o banco
 * recusa gravar uma reunião `calendar` sem o id do evento externo. Sumir com a opção esconderia a
 * dívida do único lugar onde alguém a procura — e no mês que vem outra pessoa reabriria a pergunta
 * do zero. O que não pode existir é o meio-termo: um controle clicável que devolve `422` ensina
 * que o portal está quebrado, e não que a integração não foi construída.
 *
 * **O horário sai com fuso.** O campo `datetime-local` não tem fuso, e o servidor recusa horário
 * sem ele com todas as letras: *"sem fuso, o mesmo campo significa horas diferentes para quem
 * marcou e para quem vai entrar"*. O que se digita aqui é lido como horário de São Paulo — o
 * mesmo que a tela exibe em todo o portal — e sai carimbado com o deslocamento daquele dia.
 *
 * **O link é `https` e é conferido dos dois lados.** Meet, Teams e Zoom são todos `https`; um
 * link `http` é um link que o navegador do cliente já trata como inseguro, e link de reunião é
 * colado à mão, que é onde o erro entra.
 */

import { useState } from "react";
import { Aviso, Botao, Campo, CampoLongo, GrupoDeOpcoes, Opcao, Segmentado } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import { interpretarConviteDaReuniao } from "../../nucleo/convite-da-reuniao";
import {
  DURACAO_MAXIMA_EM_MINUTOS,
  DURACAO_PADRAO_EM_MINUTOS,
  MODOS_DA_REUNIAO,
  ROTULO_DO_TIPO_DE_REUNIAO,
  TIPOS_DE_REUNIAO,
  paraInstanteComFuso,
  useAgendarReuniao,
  type TipoDeReuniao,
} from "../../nucleo/reuniao";

export interface PropsDoFormularioDaReuniao {
  implantacaoId: string;
  modulos?: readonly { chave: string; nome: string }[];
  /** Fecha o formulário depois de marcar. Ausente deixa o formulário aberto para a próxima. */
  aoConcluir?: () => void;
}

const PREFIXO_SEGURO = "https://";

const TIPOS = TIPOS_DE_REUNIAO.map((valor) => ({
  valor,
  rotulo: ROTULO_DO_TIPO_DE_REUNIAO[valor] ?? valor,
}));

type Visibilidade = "cliente" | "interna";

const VISIBILIDADES: ReadonlyArray<{ valor: Visibilidade; rotulo: string }> = [
  { valor: "cliente", rotulo: "Visível ao cliente" },
  { valor: "interna", rotulo: "Somente equipe" },
];

export function FormularioDaReuniao({ implantacaoId, modulos = [], aoConcluir }: PropsDoFormularioDaReuniao) {
  const [convite, setConvite] = useState("");
  const [assunto, setAssunto] = useState("");
  const [tipo, setTipo] = useState<TipoDeReuniao>("kickoff");
  const [quando, setQuando] = useState("");
  const [duracao, setDuracao] = useState(String(DURACAO_PADRAO_EM_MINUTOS));
  const [link, setLink] = useState("");
  const [visibilidade, setVisibilidade] = useState<Visibilidade>("cliente");
  const [moduloChave, setModuloChave] = useState("");
  const agendar = useAgendarReuniao(implantacaoId);

  const minutos = Number(duracao);
  const linkLimpo = link.trim();
  const linkInseguro = linkLimpo.length > 0 && !linkLimpo.startsWith(PREFIXO_SEGURO);
  const duracaoInvalida =
    !Number.isInteger(minutos) || minutos <= 0 || minutos > DURACAO_MAXIMA_EM_MINUTOS;
  const podeMarcar = assunto.trim().length > 0 && quando.length > 0 && !duracaoInvalida && !linkInseguro
    && (tipo === "kickoff" || modulos.length === 0 || Boolean(moduloChave));

  const conviteInterpretado = convite.trim() ? interpretarConviteDaReuniao(convite) : null;

  function lerConvite(texto: string) {
    setConvite(texto);
    if (!texto.trim()) return;
    const lido = interpretarConviteDaReuniao(texto);
    if (lido.assunto) setAssunto(lido.assunto);
    if (lido.quando) setQuando(lido.quando);
    if (lido.duracaoMin !== null) setDuracao(String(lido.duracaoMin));
    if (lido.link) setLink(lido.link);
  }

  function enviar() {
    const instante = paraInstanteComFuso(quando);
    if (!instante) return;
    agendar.mutate(
      {
        assunto: assunto.trim(),
        tipo,
        ...(tipo !== "kickoff" && moduloChave ? { modulo_chave: moduloChave } : {}),
        agendada_para: instante,
        duracao_min: minutos,
        link: linkLimpo || null,
        // Sempre `manual`: é o único modo que existe deste lado, e a tela não finge o contrário.
        modo: "manual",
        visivel_ao_cliente: visibilidade === "cliente",
      },
      {
        onSuccess: () => {
          setQuando("");
          setAssunto("");
          setConvite("");
          setLink("");
          aoConcluir?.();
        },
      },
    );
  }

  return (
    <div className="mb-4" data-testid="formulario-da-reuniao">
      <CampoLongo
        rotulo="Convite do Google Calendar"
        value={convite}
        onChange={(evento) => lerConvite(evento.target.value)}
        placeholder={"Reunião de acompanhamento\n8 de outubro de 2026, 14:00 – 15:30\nhttps://meet.google.com/…"}
        ajuda="Cole o convite inteiro. O portal preenche assunto, data, horário, duração e o primeiro link; você pode revisar tudo antes de registrar."
        rows={5}
        data-testid="convite-da-reuniao"
      />

      {conviteInterpretado && conviteInterpretado.ausentes.length > 0 ? (
        <Aviso tom="atencao" className="mb-3" data-testid="convite-incompleto">
          Não encontrei {conviteInterpretado.ausentes.join(", ")} no convite. Preencha esses
          campos manualmente antes de registrar.
        </Aviso>
      ) : conviteInterpretado ? (
        <Aviso tom="sucesso" className="mb-3" data-testid="convite-interpretado">
          Convite lido. Confira os campos abaixo antes de registrar.
        </Aviso>
      ) : null}

      <Campo
        rotulo="Assunto"
        value={assunto}
        onChange={(evento) => setAssunto(evento.target.value)}
        maxLength={200}
        obrigatorio
        data-testid="assunto-da-reuniao"
      />

      <Segmentado<TipoDeReuniao>
        rotulo="Tipo da reunião"
        itens={TIPOS}
        valor={tipo}
        aoMudar={setTipo}
        className="mb-3"
      />

      {modulos.length > 0 && tipo !== "kickoff" ? (
        <GrupoDeOpcoes rotulo="Módulo desta reunião" className="mb-3">
          {modulos.map((modulo) => (
            <Opcao key={modulo.chave} tipo="radio" name="modulo-da-reuniao"
              titulo={modulo.nome} checked={moduloChave === modulo.chave}
              onChange={() => setModuloChave(modulo.chave)} />
          ))}
        </GrupoDeOpcoes>
      ) : null}

      <div className="grade-do-agendamento">
        <Campo
          rotulo="Data e hora"
          type="datetime-local"
          value={quando}
          onChange={(evento) => setQuando(evento.target.value)}
          ajuda="Horário de Brasília — o mesmo que o cliente lê no portal dele."
          obrigatorio
          data-testid="quando-da-reuniao"
        />
        <Campo
          rotulo="Duração em minutos"
          type="number"
          min={1}
          max={DURACAO_MAXIMA_EM_MINUTOS}
          value={duracao}
          onChange={(evento) => setDuracao(evento.target.value)}
          erro={duracaoInvalida ? `Entre 1 e ${DURACAO_MAXIMA_EM_MINUTOS} minutos.` : undefined}
          ajuda="Confira a duração prevista para esta reunião."
          data-testid="duracao-da-reuniao"
        />
      </div>

      <Campo
        rotulo="Link da sala (opcional)"
        type="url"
        value={link}
        onChange={(evento) => setLink(evento.target.value)}
        placeholder="https://meet.google.com/…"
        erro={linkInseguro ? "O link precisa começar com https://." : undefined}
        ajuda="Cole o link da sala que você criou no seu calendário. O cliente vê este link."
        data-testid="link-da-reuniao"
      />

      <Segmentado<Visibilidade>
        rotulo="Visibilidade da reunião"
        itens={VISIBILIDADES}
        valor={visibilidade}
        aoMudar={setVisibilidade}
        className="mb-3"
      />
      <p className="nota-rodape mb-3">
        Reuniões internas não aparecem na Agenda do cliente nem geram material visível para ele.
      </p>

      {/* A dívida onde ela é procurada: dentro do formulário de marcar. `calendar` aparece
          **desabilitado e com o motivo por extenso** em vez de sumir — escondê-lo esconderia a
          dívida do único lugar onde alguém a procura, e no mês que vem outra pessoa reabriria a
          pergunta do zero. O que não pode existir é o meio-termo: um controle clicável que
          devolve `422`. */}
      <GrupoDeOpcoes rotulo="Como esta reunião é marcada" className="mt-2">
        {MODOS_DA_REUNIAO.map((modo) => (
          <Opcao
            key={modo.valor}
            className={modo.disponivel ? undefined : "opcao--indisponivel"}
            tipo="radio"
            name="modo-da-reuniao"
            checked={modo.disponivel}
            disabled={!modo.disponivel}
            readOnly
            titulo={modo.rotulo}
            descricao={
              modo.disponivel ? (
                "O portal guarda o horário, a duração e o link, e avisa o cliente pela esteira dele. Quem cria o evento e a sala é você, no seu calendário."
              ) : (
                <span className="opcao__motivo">{modo.motivo}</span>
              )
            }
            etiqueta={modo.disponivel ? undefined : "indisponível"}
            data-testid={`modo-${modo.valor}`}
          />
        ))}
      </GrupoDeOpcoes>

      {agendar.isError ? (
        <Aviso tom="erro" className="mb-3" data-testid="erro-do-agendamento">
          {mensagemDoConsole(agendar.error)}
        </Aviso>
      ) : null}

      <div className="acoes-do-console">
        <Botao
          disabled={agendar.isPending || !podeMarcar}
          onClick={enviar}
          data-testid="confirmar-agendamento"
        >
          {agendar.isPending ? "Registrando…" : "Registrar a reunião"}
        </Botao>
        {aoConcluir ? (
          <Botao
            variante="fantasma"
            disabled={agendar.isPending}
            onClick={aoConcluir}
            data-testid="cancelar-agendamento"
          >
            Cancelar
          </Botao>
        ) : null}
      </div>
    </div>
  );
}
